import { useQuery } from "@tanstack/react-query";
import { useApi } from "@utils/useApi";
import { StorefrontAddressClient } from "@lib/ShiOneClient";
import type { AddressSearchItemDto } from "@lib/ShiOneClient";

export interface QuoteAddressOption {
  id: number;
  name?: string;
  street: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  isDefault: boolean;
  /** Org-unit address group names this address belongs to (shi.com's "Group" filter). */
  addressGroups?: string[];
}

export function mapToQuoteAddressOption(dto: AddressSearchItemDto): QuoteAddressOption {
  const rawCountry = dto.countryCode?.trim() || dto.country?.trim() || "";
  const country = /^[A-Za-z]{2}$/.test(rawCountry) ? rawCountry.toUpperCase() : "";

  return {
    id: dto.streetAddressId ?? 0,
    name: dto.addressName || undefined,
    street: dto.address1 ?? "",
    street2: dto.address2 || undefined,
    city: dto.city ?? "",
    state: dto.state?.trim().toUpperCase() ?? "",
    postalCode: dto.postalCode ?? "",
    country,
    isDefault: dto.isDefault ?? false,
    addressGroups: dto.addressGroups?.filter((g) => g && g.trim().length > 0) ?? [],
  };
}

const QuoteAddressQueryKeys = {
  Recent: "quote-addresses-recent",
  Search: "quote-addresses-search",
} as const;

const RECENT_COUNT = 4;
const SEARCH_PAGE_SIZE = 25;

export function useRecentQuoteAddresses(orgUnitId: number | null) {
  const api = useApi(StorefrontAddressClient);

  // isInitialLoading (not isLoading) so a disabled query -- orgUnitId not yet resolved --
  // reports false instead of react-query v4's perpetual status: "loading".
  const { data, isInitialLoading, error } = useQuery({
    queryKey: [QuoteAddressQueryKeys.Recent, orgUnitId],
    queryFn: () => api.getRecentAddresses(orgUnitId ?? undefined, RECENT_COUNT),
    enabled: orgUnitId != null,
  });

  return {
    addresses: data?.addresses?.map(mapToQuoteAddressOption) ?? [],
    isLoading: isInitialLoading,
    error: error ?? null,
  };
}

export function useSearchQuoteAddresses(orgUnitId: number | null, searchText: string) {
  const api = useApi(StorefrontAddressClient);
  const trimmed = searchText.trim();

  const { data, isInitialLoading, isFetching, error } = useQuery({
    queryKey: [QuoteAddressQueryKeys.Search, orgUnitId, trimmed],
    queryFn: () =>
      api.searchAddresses(orgUnitId as number, trimmed, 1, SEARCH_PAGE_SIZE, undefined, undefined),
    enabled: orgUnitId != null && trimmed.length > 0,
    keepPreviousData: true,
  });

  return {
    addresses: data?.addresses?.map(mapToQuoteAddressOption) ?? [],
    isLoading: isInitialLoading,
    isFetching,
    error: error ?? null,
  };
}
